// Entry point → dist/build.min.js (esbuild, vanilla, bez jQuery).
//
// Blank mode = vypnuté JEN default CSS, default commerce JS Shoptetu zůstává.
// Náš JS je proto JEN enhancement vrstva nad fixním Shoptet DOM + re-bind na Shoptet eventy.
// Košík VÝHRADNĚ přes shoptet.cartShared.addToCart (viz shoptet-hooks.js).
'use strict';

import { onReady, onShoptetDom } from './shoptet-hooks.js';
import { injectSprite, scheduleLogos } from './modules/injectSprite.js';
import { initStickyHeader } from './modules/stickyHeader.js';
import { initCartCounter } from './modules/cartCounter.js';
import { initCartToast } from './modules/cartToast.js';
import { initAjaxFeedback } from './modules/ajaxFeedback.js';
import { initCartModal } from './modules/cartModal.js';
import { initMobileHeader } from './modules/mobileHeader.js';
import { initMobileNav } from './modules/mobileNav.js';
import { initMobileSearch } from './modules/mobileSearch.js';
import { initSidebarCatsToggle } from './modules/sidebarCatsToggle.js';
import { initListingTools } from './modules/listingTools.js';
import { initCategoryPage } from './modules/categoryPage.js';
import { initMobileFilter } from './modules/mobileFilter.js';
import { initProductCards } from './modules/productCard.js';
import { initSpecChips } from './modules/specChips.js';
import { initCardExtras } from './modules/cardExtras.js';
import { initWholesaleCards } from './modules/wholesalePrice.js';
import { initTopBarLinks } from './modules/topBarLinks.js';
import { initCountrySwitch } from './modules/countrySwitch.js';
import { initPromoBar } from './modules/promoBar.js';
import { initBenefitPromo } from './modules/benefitPromo.js';
import { initCampaignTone, initCampaignTracking } from './modules/campaign.js';
import { initHeaderTools, markPhones } from './modules/headerTools.js';
import { initMegaMenu } from './modules/megaMenu.js';
import { initSiteFooter } from './modules/siteFooter.js';
import { initFooterAccordion } from './modules/footerAccordion.js';
import { initBottomNav } from './modules/bottomNav.js';
import { initArticlesPage } from './modules/articlesPage.js';
import { initArticleDetail } from './modules/articleDetail.js';
import { initHomepage } from './modules/homepage.js';
import { initProductDetail } from './modules/productDetail.js';
import { initVariantPicker } from './modules/variantPicker.js';
import { initGalleryLightbox } from './modules/galleryLightbox.js';
import { initStickyAtc } from './modules/stickyAtc.js';
import { initAuthPage } from './modules/authPage.js';
import { initSearchPage } from './modules/searchPage.js';
import { initSaleBrowse } from './modules/saleBrowse.js';
import { initSearchAttribution } from './modules/searchAnalytics.js';
import { initBrandsPage } from './modules/brandsPage.js';
import { initBrandDetail } from './modules/brandDetail.js';
import { initSearchWhisperer } from './modules/searchWhisperer.js';
import { initNotFoundPage } from './modules/notFoundPage.js';
import { initCustomerPage } from './modules/customerPage.js';
import { initB2bPage } from './modules/b2bPage.js';
import { initServicePage } from './modules/servicePage.js';
import { initRetinoPage } from './modules/retinoPage.js';
import { initRatingsPage } from './modules/ratingsPage.js';
import { initContactPage } from './modules/contactPage.js';
import { initShippingPage } from './modules/shippingPage.js';
import { initStorePage } from './modules/storePage.js';
import { initOrderStatusPage } from './modules/orderStatusPage.js';
import { initFaqPage } from './modules/faqPage.js';
import { initPageHero } from './modules/pageHero.js';
import { initCheckoutPage } from './modules/checkoutPage.js';
import { initDevInstance } from './modules/devInstance.js';
import { initSupportChat } from './modules/supportChat.js';
import { ensurePageCss } from './modules/pageCss.js';

// Sem přibývají shop-specifické moduly (mega menu, hero, finder, detail, checkout…).
// Vzory hledej v batteryshop repu: megaMenu.js, categoryHero.js, productDetail.js,
// checkoutExtras.js, homepageDom.js (mobilní UA render!).

// Každý init běží izolovaně: výjimka v jednom modulu nesmí zastavit ty za ním
// (do 2026-09-09 stála pokladna i obě pojistky až na konci sekvence, takže pád
// kdekoli dřív = dvojí menu, cloak do CSS pojistky a pokladna bez naší vrstvy).
// Hlášení jde do konzole s prefixem `[bs]` — smoke i js-health ji hlídají.
// Kostru stránky (`html.bs-boot`) žádný init nedrží: CSS kostry počítá s NATIVNÍM
// DOM, takže nad DOM, který boot už přestavěl, se rozpadne (/slevoakce/ s bránou
// saleBrowse 7.–8. 10. 2026: posunutá hlavička, zdvojené drobečky, bílá plocha).
// Modul, který čeká na data, drží vlastní kostru svého výřezu (saleBrowse `bs-sb-wait`).
function safe(name, fn) {
  try {
    fn();
  } catch (e) {
    try {
      console.error('[bs] init ' + name + ' selhal', e);
    } catch (err) { /* konzole není */ }
  }
}

// Pořadí je nosné (komentáře „po X") — inity sahají na DOM, který postavil předchůdce.
const BOOT = [
  ['injectSprite', injectSprite],
  // barevná loga (patička, pokladna, modal doručení) z CDN až po `load` — #313;
  // kdo je potřebuje dřív, volá `ensureLogos()` sám (checkoutPage, shippingModal)
  ['logosSprite', scheduleLogos],
  ['markPhones', markPhones], // html.bs-no-phone dřív, než cokoliv kreslí telefon
  ['campaignTone', initCampaignTone], // html.bs-camp-ink (Black Friday) dřív, než se kreslí pruh a menu
  ['promoBar', initPromoBar],
  ['benefitPromo', initBenefitPromo], // USP pás v okně akce na dopravu (#322), mimo okno nic
  ['campaignTracking', initCampaignTracking], // proklik menu na plochy kampaně (GA4), delegovaný na dokumentu
  ['topBarLinks', initTopBarLinks],
  ['countrySwitch', initCountrySwitch], // po topBarLinks — vlajky CZ ↔ SK stojí za jeho řadou
  ['headerTools', initHeaderTools],
  ['megaMenu', initMegaMenu],
  ['stickyHeader', initStickyHeader],
  ['cartCounter', initCartCounter],
  ['cartToast', initCartToast],
  ['ajaxFeedback', initAjaxFeedback], // hlášky AJAX akcí, které default JS nechává němé (#224)
  ['cartModal', initCartModal],
  ['mobileSearch', initMobileSearch], // před mobileHeader — lupa v hlavičce na něj rovnou váže klik
  ['mobileHeader', initMobileHeader],
  ['mobileNav', initMobileNav],
  ['sidebarCatsToggle', initSidebarCatsToggle],
  ['listingTools', initListingTools],
  ['categoryPage', initCategoryPage], // po listingTools — toolbar musí být v #category-header dřív, než se staví sloupce
  ['brandDetail', initBrandDetail], // po categoryPage — hlavička značky obaluje jeho .bs-cat-head a perex
  ['mobileFilter', initMobileFilter], // po categoryPage — lišta se věší za #category-header, až stojí sloupce
  ['productCards', initProductCards], // po injectSprite — karta si tahá ikony ze spritu
  ['specChips', initSpecChips], // po initProductCards — chipy se věší do hotové karty (výpis kategorie)
  ['cardExtras', initCardExtras], // po initProductCards — konec akce v pilulce + Bestseller z indexu (všechny karty)
  ['wholesaleCards', initWholesaleCards], // po initProductCards — VO cena proti běžné v pilulce (jen přihlášený velkoobchod)
  // procházení výprodeje z indexu (přepínač saleBrowse) — po categoryPage, listingTools,
  // mobileFilter a kartách; do odpovědi indexu (max ~1,2 s) kreslí kostru výpisu sám
  ['saleBrowse', initSaleBrowse],
  ['productDetail', initProductDetail], // po initProductCards — související produkty už jsou karty
  ['variantPicker', initVariantPicker], // po initProductDetail — dostupnost variant už stojí v pilulce meta řádku
  ['galleryLightbox', initGalleryLightbox], // vlastní lightbox galerie (capture klik dřív než colorbox)
  ['stickyAtc', initStickyAtc], // po initProductDetail — lišta se váže na `.bs-buy__row`
  ['siteFooter', initSiteFooter],
  ['footerAccordion', initFooterAccordion], // po siteFooter — akordeony visí na jeho sloupcích
  ['bottomNav', initBottomNav], // spodní navigace (mobil); otevírá drawer i fullscreen hledání
  // live chat SupportBoxu (přepínač supportChat): tlačítko + delegovaný klik na
  // vstupy `data-bs-chat`, které si postavily moduly výš (karta poradce na detailu)
  ['supportChat', initSupportChat],
  ['articlesPage', initArticlesPage], // výpis rubriky blogu (/rubriky/<slug>/, type-posts-listing)
  ['articleDetail', initArticleDetail], // detail článku (type-post) — hero z meta, TOC, prev/next, pás z rubriky
  ['homepage', initHomepage], // po initProductCards — carousely přeskládávají už hotové karty
  ['authPage', initAuthPage], // /login/, /registrace/, /klient/zapomenute-heslo/
  ['searchAttribution', initSearchAttribution], // GA4 `search_session` z uložené značky, před prvním display
  ['searchPage', initSearchPage], // po initProductCards — mřížka výsledků staví na kartách
  ['searchWhisperer', initSearchWhisperer], // našeptávač v hlavičce (obsah plní default JS)
  ['brandsPage', initBrandsPage], // A–Z přehled značek (/znacka/)
  ['notFoundPage', initNotFoundPage], // 404 — rozcestník místo nativní hlášky (po initProductCards)
  ['customerPage', initCustomerPage], // klientská sekce (/klient/*) — po initAuthPage, sdílí body.customer-page
  // info stránky II (#38) — každá si sama ověří body.in-<slug>, takže se pustí
  // právě jedna; obsah čtou z administrace (`article.pageArticleDetail`)
  ['b2bPage', initB2bPage], // /b2b/ — úrovně spolupráce z `.b2b-*` bloků administrace
  ['servicePage', initServicePage], // /servis-naradi/ — ceník a značky z textu administrace
  ['retinoPage', initRetinoPage], // /reklamacni-formular/ i /odstoupeni-ve-lhute-14-dnu/ (Retino iframe)
  ['ratingsPage', initRatingsPage], // /hodnoceni-obchodu/ — nativní výpis hodnocení Shoptetu
  // info stránky I (#37) — kontakt, doprava a platba, prodejna; obsah (ceny,
  // otevírací doba, fotky, mapa) čtou z administrace, stejně jako #38
  ['contactPage', initContactPage], // /kontakt/ — karty, galerie, video, mapa
  ['shippingPage', initShippingPage], // /doprava-platba/ — ceník dopravy a platby + akordeony
  ['storePage', initStorePage], // /prodejna-naradi/ — hero prodejny, sortiment, mapa, galerie
  // /stav-objednavky/ (#461): formulář a stav objednávky z API `order-status` na s1
  ['orderStatusPage', initOrderStatusPage],
  // /caste-dotazy/ — rám kolem otázek z administrace (nativní <details> akordeon)
  ['faqPage', initFaqPage],
  // hlavička s fotkou (#320) — PO všech info stránkách: jednosloupcové hero, které
  // postavily, přesune do pásu s fotkou; jen se záznamem stránky v admin bloku
  // `#bs-page-photos`, bez něj stránka zůstává beze změny (kontakt si pás staví sám)
  ['pageHero', initPageHero],
  // pokladna (#16) — MUSÍ být po hlavičce i patičce: slim chrome jim bere místo
  // (logo se stěhuje z `#header`) a CSS je pod `html.bs-co-ready` schová
  ['checkoutPage', initCheckoutPage],
  // testovací instance (dev 797093): pruh + blokace odeslání objednávky — po
  // checkoutPage, hláška se věší nad jeho hotový blok CTA (incident 2026-09-21)
  ['devInstance', initDevInstance],
];

function boot() {
  // Verze nasazeného bundlu pro periodické testy (tools/qa/assets-version.mjs):
  // esbuild ji dosadí z git short SHA (build.mjs `define`), v Benderu/bez gitu je `dev`.
  try {
    document.documentElement.dataset.bsBuild = typeof __BS_BUILD__ === 'string' ? __BS_BUILD__ : 'dev';
  } catch (e) { /* bez stampu se nic neděje */ }

  // Stránkový CSS mimo main.css (#313, pageCss.js): hlavička ho vkládá podle
  // URL/meta, tady je pojistka podle body tříd (přejmenovaný slug, offline
  // fixture). Vrací Promise jen když se na link ještě čeká — pak cloak zůstává,
  // dokud CSS nedorazí (max 4 s), jinak by se ukázala rozsypaná pokladna.
  // Inity se tím NEzdržují — stavějí DOM, který se odkryje až s hotovým CSS.
  let pageCssReady = null;
  try {
    pageCssReady = ensurePageCss();
  } catch (e) {
    try { console.error('[bs] init pageCss selhal', e); } catch (err) { /* konzole není */ }
  }

  for (const [name, fn] of BOOT) safe(name, fn);

  // Naše hlavička stojí → teprve teď smí CSS skrýt nativní topbar menu a #navigation
  // na desktopu. Když hlavička nestojí, zůstane uživateli funkční nativní menu.
  if (document.querySelector('.bs-catbar') && document.querySelector('.bs-topbar-links')) {
    document.documentElement.classList.add('bs-header-ready');
  }

  // boot cloak (skeleton z HTML kódu hlavičky) — reshuffle hotový, odkrýt.
  // Pojistka proti spadlému bootu je v inline skriptu hlavičky (1,5 s po
  // `load`, tvrdý strop 20 s). Se stránkovým CSS na cestě se odkrytí odloží
  // do jeho `load` (viz pageCssReady výš); bez něj hned, synchronně. Na stránce
  // s filtrem se odkrytí za `ready` odkládat nesmí: default JS při `ready` volá
  // `detectFilters()` (main-3g.js) a skrytý `.sidebar .box-filters` znamená přesun
  // filtru do `#filters-wrapper` v obsahu, pod toolbar (8. 10. 2026, #538).
  const uncloak = () => document.documentElement.classList.remove('bs-boot');
  if (pageCssReady) pageCssReady.then(uncloak, uncloak);
  else uncloak();
}

onReady(boot);

// Po AJAX překreslení (Shoptet výpis/obsah) re-init idempotentních částí.
// POZOR: `onShoptetDom` chytá i generický `ShoptetDOMContentLoaded`, který
// default JS (`signalDomLoad`) vydává PŘED každým specifickým eventem — tedy
// po filtru, po loadMore i po košíkovém AJAXu; při prostém načtení stránky ne.
// Každý init tu proto musí být idempotentní (ověřeno 2026-09-09, B4-02).
const REINIT = [
  ['injectSprite', injectSprite],
  ['sidebarCatsToggle', initSidebarCatsToggle],
  ['listingTools', initListingTools],
  ['categoryPage', initCategoryPage],
  ['brandDetail', initBrandDetail],
  ['mobileFilter', initMobileFilter],
  ['productCards', initProductCards],
  // po filtru default JS vymění `.products` blok i s observerem z bootu → chipy
  // se musí navěsit na nový (code review 2026-09-09, B3-01)
  ['specChips', initSpecChips],
  ['cardExtras', initCardExtras],
  ['wholesaleCards', initWholesaleCards],
];
onShoptetDom(function () {
  for (const [name, fn] of REINIT) safe(name, fn);
});
